import { getMessaging, getToken, isSupported, type Messaging } from 'firebase/messaging';
import { firebaseApp } from './firebase';
import { notificationApi } from './api/notification.api';

let messagingInstance: Messaging | null = null;

async function getMessagingSafe(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;
  if (!(await isSupported())) return null;
  messagingInstance = getMessaging(firebaseApp);
  return messagingInstance;
}

/**
 * Asks the browser for notification permission and registers an FCM token
 * with the backend. Returns the token on success, or null when the browser
 * does not support push or the user denied permission.
 */
export async function requestAndRegisterPushToken(tenantId: string): Promise<string | null> {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
  if (!vapidKey) {
    throw new Error('VAPID key not configured (VITE_FIREBASE_VAPID_KEY).');
  }
  const messaging = await getMessagingSafe();
  if (!messaging) {
    throw new Error('Este navegador no soporta notificaciones push.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return null;
  }
  const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: swRegistration,
  });
  if (!token) return null;
  await notificationApi.registerPushToken({ tenantId, token });
  return token;
}

export async function unregisterCurrentPushToken(tenantId: string): Promise<void> {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
  if (!vapidKey) return;
  const messaging = await getMessagingSafe();
  if (!messaging) return;
  try {
    const token = await getToken(messaging, { vapidKey });
    if (token) {
      await notificationApi.unregisterPushToken({ tenantId, token });
    }
  } catch {
    // ignore
  }
}
